import { z } from "zod";

import { assistantImageGenerationAspectRatioSchema } from "./generated-content.ts";

export const BRAND_ASSISTANT_SELECTED_ASSET_REFERENCE_PART_TYPE =
  "data-selected-generated-asset-reference";
export const BRAND_ASSISTANT_SELECTED_IMAGE_REFERENCE_PART_TYPE =
  "data-selected-generated-image-reference";
export const BRAND_ASSISTANT_CHAT_COMPACTION_EVENT_PART_TYPE =
  "data-brand-assistant-chat-compaction-event";
export const BRAND_ASSISTANT_ACTIVITY_UPDATE_PART_TYPE = "data-brand-assistant-activity-update";
export const BRAND_ASSISTANT_PROFILE_PREVIEW_RESOLUTION_PART_TYPE =
  "data-brand-assistant-profile-preview-resolution";
export const BRAND_ASSISTANT_ONBOARDING_COMPLETION_PART_TYPE =
  "data-brand-assistant-onboarding-completion";
export const BRAND_ASSISTANT_TIPS_PART_TYPE = "data-brand-assistant-tips";
export const BRAND_ASSISTANT_SUGGESTION_ACTION_PART_TYPE = "data-brand-assistant-suggestion-action";
export const BRAND_ASSISTANT_STREAM_METADATA_KEY = "brandAssistantStream";

export const BRAND_ASSISTANT_CREATION_PREVIEW_TOOL_NAMES = [
  "preview_standalone_asset_batch_creation",
  "preview_campaign_creation",
  "generate_branded_image",
] as const;
export const brandAssistantCreationPreviewToolNameSchema = z.enum(
  BRAND_ASSISTANT_CREATION_PREVIEW_TOOL_NAMES,
);

export const brandAssistantSelectedAssetReferenceSchema = z.object({
  generatedAssetId: z.uuid(),
  batchId: z.uuid(),
  batchSourceType: z.enum(["standalone", "campaign"]).nullable().optional(),
  batchName: z.string().trim().min(1).max(200),
  scheduledPostId: z.uuid().nullable(),
  blockId: z.string().trim().min(1).max(120),
  imageSlotLabel: z.string().trim().min(1).max(120).nullable().optional(),
  imagePositionHint: z.string().trim().min(1).max(120).nullable().optional(),
  imageSlotIndex: z.number().int().positive().nullable().optional(),
  slideIndex: z.number().int().positive().nullable(),
  slideId: z.string().trim().min(1).max(120).nullable(),
  prompt: z.string().trim().min(1).max(2_000).nullable(),
  caption: z.string().trim().min(1).max(2_200).nullable().optional(),
  hashtags: z.array(z.string().trim().min(1).max(80)).max(30).nullable().optional(),
  draftUpdatedAt: z.iso.datetime({ offset: true }).nullable().optional(),
  signedUrl: z.url(),
});

export const brandAssistantSelectedAssetReferencePartSchema = z.object({
  type: z.literal(BRAND_ASSISTANT_SELECTED_ASSET_REFERENCE_PART_TYPE),
  data: brandAssistantSelectedAssetReferenceSchema,
});
export const brandAssistantSelectedImageReferenceSchema = z.object({
  generatedAssetId: z.uuid(),
  batchId: z.uuid(),
  prompt: z.string().trim().min(1).max(2_000).nullable(),
  aspectRatio: assistantImageGenerationAspectRatioSchema.nullable(),
  signedUrl: z.url(),
});
export const brandAssistantSelectedImageReferencePartSchema = z.object({
  type: z.literal(BRAND_ASSISTANT_SELECTED_IMAGE_REFERENCE_PART_TYPE),
  data: brandAssistantSelectedImageReferenceSchema,
});
export const brandAssistantChatCompactionEventDataSchema = z.object({
  schemaVersion: z.literal(1),
  summarizedUntilMessageId: z.string().trim().min(1),
  summarizedMessageCount: z.number().int().nonnegative(),
});
export const brandAssistantChatCompactionEventPartSchema = z.object({
  type: z.literal(BRAND_ASSISTANT_CHAT_COMPACTION_EVENT_PART_TYPE),
  data: brandAssistantChatCompactionEventDataSchema,
});
export const brandAssistantActivityUpdateDataSchema = z.object({
  schemaVersion: z.literal(1),
  kind: z.enum(["research", "workspace_lookup", "analysis", "preparing_preview", "generation"]),
  summary: z.string().trim().min(1).max(240),
  generatedAt: z.iso.datetime({ offset: true }),
});
export const brandAssistantActivityUpdatePartSchema = z.object({
  type: z.literal(BRAND_ASSISTANT_ACTIVITY_UPDATE_PART_TYPE),
  data: brandAssistantActivityUpdateDataSchema,
});
export const brandAssistantProfilePreviewResolutionDataSchema = z.object({
  schemaVersion: z.literal(1),
  previewId: z.string().trim().min(1).max(120),
  status: z.enum(["applied", "dismissed"]),
  resolvedAt: z.iso.datetime({ offset: true }),
});
export const brandAssistantProfilePreviewResolutionPartSchema = z.object({
  type: z.literal(BRAND_ASSISTANT_PROFILE_PREVIEW_RESOLUTION_PART_TYPE),
  data: brandAssistantProfilePreviewResolutionDataSchema,
});
export const brandAssistantOnboardingCompletionDataSchema = z.object({
  schemaVersion: z.literal(1),
  kind: z.enum([
    "brand_scan_in_progress",
    "background_complete",
    "strategy_ready",
    "strategy_generation_in_progress",
    "profile_updates_pending",
    "profile_updates_resolved",
    "visual_identity_ready",
    "first_asset_pending",
    "first_asset_cta",
  ]),
  generatedAt: z.iso.datetime({ offset: true }),
  title: z.string().trim().min(1).max(120),
  summary: z.string().trim().min(1).max(600),
  ctaLabel: z.string().trim().min(1).max(80).nullable().optional(),
  ctaPrompt: z.string().trim().min(1).max(1_000).nullable().optional(),
  visualIdentity: z
    .object({
      logoUrl: z.url().nullable(),
      colorPalette: z.array(z.string().trim().min(1)).max(16),
      secondaryColorPalette: z.array(z.string().trim().min(1)).max(16),
      typographyStyle: z.unknown().nullable(),
      visualMood: z.string().trim().min(1).nullable(),
      imageStyle: z.string().trim().min(1).nullable(),
      logoStyle: z.string().trim().min(1).nullable(),
      designConsistencyLevel: z.string().trim().min(1).nullable(),
      visualIdentityDescription: z.string().trim().min(1).nullable(),
    })
    .optional(),
});
export const brandAssistantOnboardingCompletionPartSchema = z.object({
  type: z.literal(BRAND_ASSISTANT_ONBOARDING_COMPLETION_PART_TYPE),
  data: brandAssistantOnboardingCompletionDataSchema,
});
const brandAssistantLegacyTipSchema = z.object({
  label: z.string().trim().min(1).max(48),
  prompt: z.string().trim().min(5).max(600),
});
export const brandAssistantTipSchema = brandAssistantLegacyTipSchema.extend({
  creationTool: brandAssistantCreationPreviewToolNameSchema.nullable(),
});
const brandAssistantTipsDataV1Schema = z.object({
  schemaVersion: z.literal(1),
  title: z.string().trim().min(1).max(80).nullable().optional(),
  tips: z.array(brandAssistantLegacyTipSchema).min(2).max(5),
});
const brandAssistantTipsDataV2Schema = z.object({
  schemaVersion: z.literal(2),
  title: z.string().trim().min(1).max(80).nullable().optional(),
  tips: z.array(brandAssistantTipSchema).min(2).max(5),
});
export const brandAssistantTipsDataSchema = z.discriminatedUnion("schemaVersion", [
  brandAssistantTipsDataV1Schema,
  brandAssistantTipsDataV2Schema,
]);
export const brandAssistantTipsPartSchema = z.object({
  type: z.literal(BRAND_ASSISTANT_TIPS_PART_TYPE),
  data: brandAssistantTipsDataSchema,
});
export const brandAssistantSuggestionActionDataSchema = z.object({
  schemaVersion: z.literal(1),
  toolName: brandAssistantCreationPreviewToolNameSchema,
});
export const brandAssistantSuggestionActionPartSchema = z.object({
  type: z.literal(BRAND_ASSISTANT_SUGGESTION_ACTION_PART_TYPE),
  data: brandAssistantSuggestionActionDataSchema,
});
export const brandAssistantStreamMetadataSchema = z.object({
  schemaVersion: z.literal(1),
  status: z.enum(["streaming", "failed"]),
  startedAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
});

export type BrandAssistantSelectedAssetReference = z.infer<
  typeof brandAssistantSelectedAssetReferenceSchema
>;
export type BrandAssistantSelectedImageReference = z.infer<
  typeof brandAssistantSelectedImageReferenceSchema
>;

export type BrandAssistantSelectedAssetReferencePart = z.infer<
  typeof brandAssistantSelectedAssetReferencePartSchema
>;
export type BrandAssistantSelectedImageReferencePart = z.infer<
  typeof brandAssistantSelectedImageReferencePartSchema
>;
export type BrandAssistantChatCompactionEventData = z.infer<
  typeof brandAssistantChatCompactionEventDataSchema
>;
export type BrandAssistantChatCompactionEventPart = z.infer<
  typeof brandAssistantChatCompactionEventPartSchema
>;
export type BrandAssistantActivityUpdateData = z.infer<
  typeof brandAssistantActivityUpdateDataSchema
>;
export type BrandAssistantActivityUpdatePart = z.infer<
  typeof brandAssistantActivityUpdatePartSchema
>;
export type BrandAssistantProfilePreviewResolutionData = z.infer<
  typeof brandAssistantProfilePreviewResolutionDataSchema
>;
export type BrandAssistantProfilePreviewResolutionPart = z.infer<
  typeof brandAssistantProfilePreviewResolutionPartSchema
>;
export type BrandAssistantOnboardingCompletionData = z.infer<
  typeof brandAssistantOnboardingCompletionDataSchema
>;
export type BrandAssistantOnboardingCompletionPart = z.infer<
  typeof brandAssistantOnboardingCompletionPartSchema
>;
export type BrandAssistantCreationPreviewToolName = z.infer<
  typeof brandAssistantCreationPreviewToolNameSchema
>;
export type BrandAssistantTip =
  | z.infer<typeof brandAssistantLegacyTipSchema>
  | z.infer<typeof brandAssistantTipSchema>;
export type BrandAssistantTipsData = z.infer<typeof brandAssistantTipsDataSchema>;
export type BrandAssistantTipsPart = z.infer<typeof brandAssistantTipsPartSchema>;
export type BrandAssistantSuggestionActionData = z.infer<
  typeof brandAssistantSuggestionActionDataSchema
>;
export type BrandAssistantSuggestionActionPart = z.infer<
  typeof brandAssistantSuggestionActionPartSchema
>;
export type BrandAssistantStreamMetadata = z.infer<typeof brandAssistantStreamMetadataSchema>;

export const buildBrandAssistantSelectedAssetReferencePart = (
  reference: BrandAssistantSelectedAssetReference,
): BrandAssistantSelectedAssetReferencePart => ({
  type: BRAND_ASSISTANT_SELECTED_ASSET_REFERENCE_PART_TYPE,
  data: reference,
});

export const buildBrandAssistantSelectedImageReferencePart = (
  reference: BrandAssistantSelectedImageReference,
): BrandAssistantSelectedImageReferencePart => ({
  type: BRAND_ASSISTANT_SELECTED_IMAGE_REFERENCE_PART_TYPE,
  data: reference,
});

export const parseBrandAssistantSelectedAssetReferencePart = (
  value: unknown,
): BrandAssistantSelectedAssetReferencePart | null => {
  const parsed = brandAssistantSelectedAssetReferencePartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseBrandAssistantSelectedImageReferencePart = (
  value: unknown,
): BrandAssistantSelectedImageReferencePart | null => {
  const parsed = brandAssistantSelectedImageReferencePartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const buildBrandAssistantChatCompactionEventPart = (
  data: BrandAssistantChatCompactionEventData,
): BrandAssistantChatCompactionEventPart => ({
  type: BRAND_ASSISTANT_CHAT_COMPACTION_EVENT_PART_TYPE,
  data,
});

export const buildBrandAssistantActivityUpdatePart = (
  data: BrandAssistantActivityUpdateData,
): BrandAssistantActivityUpdatePart => ({
  type: BRAND_ASSISTANT_ACTIVITY_UPDATE_PART_TYPE,
  data,
});

export const buildBrandAssistantProfilePreviewResolutionPart = (
  data: BrandAssistantProfilePreviewResolutionData,
): BrandAssistantProfilePreviewResolutionPart => ({
  type: BRAND_ASSISTANT_PROFILE_PREVIEW_RESOLUTION_PART_TYPE,
  data,
});

export const buildBrandAssistantOnboardingCompletionPart = (
  data: BrandAssistantOnboardingCompletionData,
): BrandAssistantOnboardingCompletionPart => ({
  type: BRAND_ASSISTANT_ONBOARDING_COMPLETION_PART_TYPE,
  data,
});

export const buildBrandAssistantTipsPart = (
  data: BrandAssistantTipsData,
): BrandAssistantTipsPart => ({
  type: BRAND_ASSISTANT_TIPS_PART_TYPE,
  data,
});

export const buildBrandAssistantSuggestionActionPart = (
  toolName: BrandAssistantCreationPreviewToolName,
): BrandAssistantSuggestionActionPart => ({
  type: BRAND_ASSISTANT_SUGGESTION_ACTION_PART_TYPE,
  data: {
    schemaVersion: 1,
    toolName,
  },
});

export const parseBrandAssistantChatCompactionEventPart = (
  value: unknown,
): BrandAssistantChatCompactionEventPart | null => {
  const parsed = brandAssistantChatCompactionEventPartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseBrandAssistantActivityUpdatePart = (
  value: unknown,
): BrandAssistantActivityUpdatePart | null => {
  const parsed = brandAssistantActivityUpdatePartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseBrandAssistantProfilePreviewResolutionPart = (
  value: unknown,
): BrandAssistantProfilePreviewResolutionPart | null => {
  const parsed = brandAssistantProfilePreviewResolutionPartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseBrandAssistantOnboardingCompletionPart = (
  value: unknown,
): BrandAssistantOnboardingCompletionPart | null => {
  const parsed = brandAssistantOnboardingCompletionPartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseBrandAssistantTipsPart = (value: unknown): BrandAssistantTipsPart | null => {
  const parsed = brandAssistantTipsPartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseBrandAssistantSuggestionActionPart = (
  value: unknown,
): BrandAssistantSuggestionActionPart | null => {
  const parsed = brandAssistantSuggestionActionPartSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const parseBrandAssistantStreamMetadata = (
  message: unknown,
): BrandAssistantStreamMetadata | null => {
  if (!message || typeof message !== "object" || Array.isArray(message)) {
    return null;
  }

  const metadata = (message as { metadata?: unknown }).metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return null;
  }

  const parsed = brandAssistantStreamMetadataSchema.safeParse(
    (metadata as Record<string, unknown>)[BRAND_ASSISTANT_STREAM_METADATA_KEY],
  );
  return parsed.success ? parsed.data : null;
};

export const hasActiveBrandAssistantStreamMetadata = (message: unknown): boolean =>
  parseBrandAssistantStreamMetadata(message)?.status === "streaming";

export const getBrandAssistantSelectedAssetReferencesFromParts = (
  parts: readonly unknown[],
): BrandAssistantSelectedAssetReference[] =>
  parts
    .map((part) => parseBrandAssistantSelectedAssetReferencePart(part)?.data)
    .filter((reference): reference is BrandAssistantSelectedAssetReference => Boolean(reference));

export const getBrandAssistantSelectedImageReferencesFromParts = (
  parts: readonly unknown[],
): BrandAssistantSelectedImageReference[] =>
  parts
    .map((part) => parseBrandAssistantSelectedImageReferencePart(part)?.data)
    .filter((reference): reference is BrandAssistantSelectedImageReference => Boolean(reference));

export const getBrandAssistantActivityUpdatesFromParts = (
  parts: readonly unknown[],
): BrandAssistantActivityUpdateData[] =>
  parts
    .map((part) => parseBrandAssistantActivityUpdatePart(part)?.data)
    .filter((update): update is BrandAssistantActivityUpdateData => Boolean(update));

export const getBrandAssistantProfilePreviewResolutionsFromParts = (
  parts: readonly unknown[],
): BrandAssistantProfilePreviewResolutionData[] =>
  parts
    .map((part) => parseBrandAssistantProfilePreviewResolutionPart(part)?.data)
    .filter((resolution): resolution is BrandAssistantProfilePreviewResolutionData =>
      Boolean(resolution),
    );

export const getBrandAssistantOnboardingCompletionsFromParts = (
  parts: readonly unknown[],
): BrandAssistantOnboardingCompletionData[] =>
  parts
    .map((part) => parseBrandAssistantOnboardingCompletionPart(part)?.data)
    .filter((completion): completion is BrandAssistantOnboardingCompletionData =>
      Boolean(completion),
    );

export const getBrandAssistantTipsFromParts = (
  parts: readonly unknown[],
): BrandAssistantTipsData[] =>
  parts
    .map((part) => parseBrandAssistantTipsPart(part)?.data)
    .filter((tips): tips is BrandAssistantTipsData => Boolean(tips));

export const getBrandAssistantSuggestionActionFromParts = (
  parts: readonly unknown[],
): BrandAssistantSuggestionActionData | null =>
  parts
    .map((part) => parseBrandAssistantSuggestionActionPart(part)?.data)
    .findLast((action): action is BrandAssistantSuggestionActionData => Boolean(action)) ?? null;

export const getBrandAssistantTipCreationTool = (
  tip: BrandAssistantTip,
): BrandAssistantCreationPreviewToolName | null =>
  "creationTool" in tip ? tip.creationTool : null;
